import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  computeChangeBreakdown,
  formatChangeBreakdown,
} from "@/lib/cashDenominations";
import { formatCurrency, parseAmount } from "@/lib/money";

export interface QuickSaleCashPaymentModalProps {
  open: boolean;
  totalGross: number;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

function suggestReceivedAmounts(total: number): number[] {
  const suggestions = new Set<number>();
  suggestions.add(total);
  const roundedUp5 = Math.ceil(total / 5) * 5;
  const roundedUp10 = Math.ceil(total / 10) * 10;
  const roundedUp20 = Math.ceil(total / 20) * 20;
  for (const value of [roundedUp5, roundedUp10, roundedUp20, 50, 100]) {
    if (value >= total) suggestions.add(value);
  }
  return Array.from(suggestions).sort((a, b) => a - b).slice(0, 5);
}

export function QuickSaleCashPaymentModal({
  open,
  totalGross,
  submitting,
  error,
  onClose,
  onConfirm,
}: QuickSaleCashPaymentModalProps) {
  const [receivedInput, setReceivedInput] = useState("");

  useEffect(() => {
    if (open) {
      setReceivedInput(totalGross > 0 ? totalGross.toFixed(2) : "");
    }
  }, [open, totalGross]);

  const receivedAmount = parseAmount(receivedInput) ?? 0;
  const changeAmount = Math.round((receivedAmount - totalGross) * 100) / 100;
  const changeBreakdown = useMemo(
    () => (changeAmount > 0 ? computeChangeBreakdown(changeAmount) : null),
    [changeAmount],
  );
  const breakdownLines = changeBreakdown ? formatChangeBreakdown(changeBreakdown) : [];
  const canConfirm = receivedAmount >= totalGross && totalGross > 0 && !submitting;
  const suggestions = useMemo(() => suggestReceivedAmounts(totalGross), [totalGross]);

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Barzahlung"
      description="Erhaltenen Betrag eingeben und Zahlung bestätigen. Die Kassenschublade öffnet sich nach erfolgreicher Buchung."
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" disabled={submitting} onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="button" loading={submitting} disabled={!canConfirm} onClick={onConfirm}>
            Barzahlung bestätigen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-separator bg-fill-tertiary p-4">
          <p className="text-sm text-label-secondary">Zu zahlen</p>
          <p className="text-2xl font-semibold tabular-nums">
            {formatCurrency(String(totalGross.toFixed(2)))}
          </p>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          Erhalten (Bar)
          <input
            type="text"
            inputMode="decimal"
            className="eh-input text-lg tabular-nums"
            value={receivedInput}
            disabled={submitting}
            autoFocus
            onChange={(e) => setReceivedInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canConfirm) {
                e.preventDefault();
                onConfirm();
              }
            }}
          />
        </label>

        {suggestions.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((amount) => (
              <button
                key={amount}
                type="button"
                disabled={submitting}
                className="rounded-lg border border-separator bg-fill px-3 py-1.5 text-sm tabular-nums hover:bg-fill-tertiary disabled:opacity-50"
                onClick={() => setReceivedInput(amount.toFixed(2))}
              >
                {formatCurrency(String(amount.toFixed(2)))}
              </button>
            ))}
          </div>
        ) : null}

        {receivedAmount > 0 && receivedAmount < totalGross ? (
          <p className="text-sm text-status-red">
            Der erhaltene Betrag ist zu niedrig (fehlen{" "}
            {formatCurrency(String((totalGross - receivedAmount).toFixed(2)))}).
          </p>
        ) : null}

        {changeAmount > 0 ? (
          <div className="rounded-lg border border-separator bg-surface p-4">
            <p className="text-sm text-label-secondary">Rückgeld</p>
            <p className="text-xl font-semibold tabular-nums text-status-green">
              {formatCurrency(String(changeAmount.toFixed(2)))}
            </p>
            {breakdownLines.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                  Optimale Stückelung
                </p>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {breakdownLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : receivedAmount >= totalGross && totalGross > 0 ? (
          <p className="text-sm text-label-secondary">Passend bezahlt – kein Rückgeld.</p>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Modal>
  );
}
